import {
  ApplyFilterPayloadProps,
  ColumnListProps,
  DataListProps,
  ItemProps,
  RowStyles,
  TableCellComparator,
} from './Table.interface';

const getFilterKeys = (filterValues: ApplyFilterPayloadProps) =>
  Object.keys(filterValues).filter(key => filterValues[key] !== 'All');

const filterValueExists = (
  filterValue: string,
  items: ItemProps[],
  filterKey: string,
) => filterValue === items.find(({ key }) => key === filterKey)?.searchkey;

const iterateFilterKeys = (
  filterKeys: string[],
  filterValues: ApplyFilterPayloadProps,
  items: ItemProps[],
) =>
  filterKeys.reduce((accumulator, current) => {
    return filterValueExists(filterValues[current], items, current)
      ? accumulator + 1
      : 0;
  }, 0);

export const applyFilter = (
  data: DataListProps[],
  filterValues: ApplyFilterPayloadProps,
) => {
  // get filterKeys as array from filterValues
  const filterKeys = getFilterKeys(filterValues);

  return data.filter(({ items }) => {
    // iterate filterKeys to increment the counter if values met
    const counter = iterateFilterKeys(filterKeys, filterValues, items);
    return counter === filterKeys.length;
  });
};

export const changeFilter = (
  values: ApplyFilterPayloadProps,
  filterValues: ApplyFilterPayloadProps,
) => {
  const filterKey = Object.keys(values)[0]; // to get the object filter key
  const filterValue = Object.values(values)[0]; // to get the object filter value
  const filterStateValue = filterValues;
  filterStateValue[filterKey] = filterValue;

  return filterStateValue;
};

// string comparator
export const stringCellComparator: TableCellComparator<string> = (
  cell1: string,
  cell2: string,
) => {
  if (
    cell1 &&
    cell2 &&
    typeof cell1 === 'string' &&
    typeof cell2 === 'string'
  ) {
    return cell1.localeCompare(cell2);
  }
  return -1;
};

const transformItem = (items: ItemProps[], rowStyles: RowStyles) =>
  items.map(element => ({
    ...element,
    title: typeof element.content === 'string' ? element.content : '',
    styles: { ...rowStyles[element.key]?.styles }, // for each cell based on column
    truncateContent: true,
  }));

const transformData = (
  data: DataListProps[],
  headerColumns: ColumnListProps[],
) => {
  if (data.length === 0) {
    return [];
  }
  const columnStyles = headerColumns.map(({ uniqueKey, styles }) => ({
    [uniqueKey]: { styles: styles || {} },
  }));

  const rowStyles = Object.assign({}, ...columnStyles);

  return data.map(({ items, key }) => ({
    key,
    items: transformItem(items, rowStyles),
    styles: { justifyContent: 'space-between' }, // for each row
  }));
};

export const applySort = (
  sortColumn: string,
  sortDirection: number,
  headerColumns: ColumnListProps[],
  data: DataListProps[],
) => {
  if (sortColumn && sortDirection) {
    const sortColumnIndex = headerColumns.findIndex(
      column => column.uniqueKey === sortColumn,
    );
    const comparator = headerColumns[sortColumnIndex].cellComparator;
    if (comparator) {
      return transformData(data, headerColumns).sort((r1, r2) => {
        const cell1 = r1.items[sortColumnIndex];
        const cell2 = r2.items[sortColumnIndex];
        return (
          comparator(
            typeof cell1.searchkey === 'string'
              ? (cell1.searchkey as string)
              : '',
            typeof cell2.searchkey === 'string'
              ? (cell2.searchkey as string)
              : '',
          ) * sortDirection
        );
      });
    }
  }
  return transformData(data, headerColumns);
};

const removeItem = (items: string[], key: string) =>
  items.filter(item => item !== key);

export const setSelectedItems = (
  items: string[],
  key: string,
  checked: boolean,
) => {
  return checked ? [...items, key] : removeItem(items, key);
};

export const isNotEqual = (a: string[], b: string[]) =>
  JSON.stringify(a) !== JSON.stringify(b);
